import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Calendar, Ticket, MessageSquare, FileText, CreditCard, BookOpen, Server, ShieldCheck, ArrowLeft, Search, Eye, EyeOff, RefreshCw } from 'lucide-react';
type Domain = 'communication' | 'productivity' | 'storage' | 'finance' | 'infrastructure';
type Status = 'connected' | 'available';
type AuthKind = 'oauth' | 'api-key';
interface Scope {
  label: string;
  granted: boolean;
}
interface SyncRow {
  label: string;
  lastRun: string;
  nextRun: string;
  records: number;
}
interface ActionRow {
  label: string;
  description: string;
}
interface Integration {
  id: string;
  name: string;
  domain: Domain;
  authKind: AuthKind;
  icon: React.FC<{
    size?: number;
    className?: string;
    strokeWidth?: number;
  }>;
  status: Status;
  disabled?: boolean;
  disabledReason?: string;
  syncDescription: string;
  account?: string;
  lastSynced?: string;
  connectionId?: string;
  scopes?: Scope[];
  syncs?: SyncRow[];
  actions?: ActionRow[];
  outputRouting?: string[];
}
const DOMAIN_LABELS: Record<Domain, string> = {
  communication: 'Communication',
  productivity: 'Productivity',
  storage: 'Storage',
  finance: 'Finance',
  infrastructure: 'Infrastructure'
};
const INTEGRATIONS: Integration[] = [{
  id: 'gmail',
  name: 'Gmail',
  domain: 'communication',
  authKind: 'oauth',
  icon: Mail,
  status: 'connected',
  syncDescription: 'Emails, threads, labels',
  account: 'matt@contextloops.com',
  lastSynced: '5 minutes ago',
  connectionId: 'conn_8e0911fa-c1bd-4e1c-8256-68357d99e75d',
  scopes: [{
    label: 'Read emails',
    granted: true
  }, {
    label: 'Send emails',
    granted: true
  }, {
    label: 'Manage labels',
    granted: true
  }, {
    label: 'Read thread metadata',
    granted: true
  }],
  syncs: [{
    label: 'Emails',
    lastRun: '5m ago',
    nextRun: 'in 10m',
    records: 14832
  }, {
    label: 'Labels',
    lastRun: '1h ago',
    nextRun: 'in 3h',
    records: 42
  }],
  actions: [{
    label: 'Send email',
    description: 'Compose and send from the connected account'
  }, {
    label: 'Create label',
    description: 'Add a new Gmail label'
  }, {
    label: 'Apply label',
    description: 'Move a thread into a label bucket'
  }, {
    label: 'Fetch attachment',
    description: 'Download an attachment by id'
  }],
  outputRouting: ['messages', 'data']
}, {
  id: 'slack',
  name: 'Slack',
  domain: 'communication',
  authKind: 'oauth',
  icon: MessageSquare,
  status: 'available',
  syncDescription: 'Messages, channels, threads'
}, {
  id: 'whatsapp',
  name: 'WhatsApp',
  domain: 'communication',
  authKind: 'oauth',
  icon: MessageSquare,
  status: 'available',
  disabled: true,
  disabledReason: 'Needs Business API',
  syncDescription: 'Send and receive via WhatsApp Business API'
}, {
  id: 'gcal',
  name: 'Google Calendar',
  domain: 'productivity',
  authKind: 'oauth',
  icon: Calendar,
  status: 'connected',
  syncDescription: 'Events, attendees, calendars',
  account: 'matt@contextloops.com',
  lastSynced: '12 minutes ago',
  connectionId: 'conn_4b2f1e9c-9c1d-4a2e-b8f2-1a2b3c4d5e6f',
  scopes: [{
    label: 'Read events',
    granted: true
  }, {
    label: 'Create events',
    granted: true
  }, {
    label: 'Update events',
    granted: true
  }, {
    label: 'Read calendar metadata',
    granted: true
  }],
  syncs: [{
    label: 'Events',
    lastRun: '12m ago',
    nextRun: 'in 18m',
    records: 487
  }, {
    label: 'Calendars',
    lastRun: '2h ago',
    nextRun: 'in 4h',
    records: 6
  }],
  actions: [{
    label: 'Create event',
    description: 'Schedule a new calendar event'
  }, {
    label: 'Update event',
    description: 'Change time, attendees, or details'
  }, {
    label: 'Cancel event',
    description: 'Delete an event and notify attendees'
  }],
  outputRouting: ['data']
}, {
  id: 'jira',
  name: 'Jira',
  domain: 'productivity',
  authKind: 'oauth',
  icon: Ticket,
  status: 'connected',
  syncDescription: 'Issues, projects, sprints',
  account: 'matt@contextloops.atlassian.net',
  lastSynced: '2 hours ago',
  connectionId: 'conn_8e0911fa-c1bd-4e1c-8256-68357d99e75d',
  scopes: [{
    label: 'Read issues',
    granted: true
  }, {
    label: 'Create issues',
    granted: true
  }, {
    label: 'Update issues',
    granted: true
  }, {
    label: 'Read project metadata',
    granted: true
  }],
  syncs: [{
    label: 'Issues',
    lastRun: '2h ago',
    nextRun: 'in 2h',
    records: 2143
  }, {
    label: 'Projects',
    lastRun: '1d ago',
    nextRun: 'in 23h',
    records: 25
  }, {
    label: 'Issue types',
    lastRun: '1d ago',
    nextRun: 'in 23h',
    records: 18
  }],
  actions: [{
    label: 'Create issue',
    description: 'File a new ticket in a project'
  }, {
    label: 'Transition issue',
    description: 'Move an issue through workflow states'
  }, {
    label: 'Add comment',
    description: 'Post a comment on an issue'
  }],
  outputRouting: ['data', 'actions']
}, {
  id: 'confluence',
  name: 'Confluence',
  domain: 'productivity',
  authKind: 'oauth',
  icon: FileText,
  status: 'connected',
  syncDescription: 'Pages, spaces',
  account: 'matt@contextloops.atlassian.net',
  lastSynced: '4 hours ago',
  connectionId: 'conn_8e0911fa-c1bd-4e1c-8256-68357d99e75d',
  scopes: [{
    label: 'Read pages',
    granted: true
  }, {
    label: 'Read spaces',
    granted: true
  }, {
    label: 'Add comments',
    granted: true
  }],
  syncs: [{
    label: 'Pages',
    lastRun: '4h ago',
    nextRun: 'now',
    records: 1893
  }, {
    label: 'Spaces',
    lastRun: '1d ago',
    nextRun: 'in 23h',
    records: 12
  }],
  actions: [{
    label: 'Read page',
    description: 'Fetch a page by id'
  }, {
    label: 'Add inline comment',
    description: 'Comment on a specific selection'
  }],
  outputRouting: ['library']
}, {
  id: 'gdrive',
  name: 'Google Drive',
  domain: 'storage',
  authKind: 'oauth',
  icon: BookOpen,
  status: 'available',
  syncDescription: 'Documents, spreadsheets, files'
}, {
  id: 'quickbooks',
  name: 'QuickBooks',
  domain: 'finance',
  authKind: 'oauth',
  icon: CreditCard,
  status: 'available',
  syncDescription: 'Invoices, transactions, vendors'
}, {
  id: 'coolify',
  name: 'Coolify',
  domain: 'infrastructure',
  authKind: 'api-key',
  icon: Server,
  status: 'connected',
  syncDescription: 'Deploy, restart, monitor apps',
  account: 'coolify.contextloops.com',
  lastSynced: '1 hour ago',
  connectionId: 'api_cl_4f2a8d0e7c3b1f5a9e2d',
  scopes: [{
    label: 'Read application status',
    granted: true
  }, {
    label: 'Deploy applications',
    granted: true
  }, {
    label: 'Restart services',
    granted: true
  }, {
    label: 'Read server metrics',
    granted: true
  }],
  syncs: [{
    label: 'Applications',
    lastRun: '1h ago',
    nextRun: 'in 5h',
    records: 23
  }],
  actions: [{
    label: 'Deploy application',
    description: 'Trigger a fresh deploy'
  }, {
    label: 'Restart application',
    description: 'Restart a running container'
  }, {
    label: 'View logs',
    description: 'Stream application logs'
  }],
  outputRouting: ['actions']
}];
export function IntegrationsView() {
  const [search, setSearch] = useState('');
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const [justConnected, setJustConnected] = useState<Set<string>>(new Set());
  const connectingIntegration = connectingId ? INTEGRATIONS.find(i => i.id === connectingId) ?? null : null;
  let body: React.ReactNode;
  if (viewingId) {
    const integ = INTEGRATIONS.find(i => i.id === viewingId);
    if (integ) {
      body = <IntegrationDetail integration={integ} justConnected={justConnected.has(integ.id)} onBack={() => setViewingId(null)} onConnect={() => setConnectingId(integ.id)} data-id="element-1127" />;
    }
  }
  if (!body) {
    const filtered = INTEGRATIONS.filter(i => !search || i.name.toLowerCase().includes(search.toLowerCase()) || i.domain.includes(search.toLowerCase()));
    const domains = Object.keys(DOMAIN_LABELS) as Domain[];
    const grouped = domains.map(d => ({
      domain: d,
      label: DOMAIN_LABELS[d],
      items: filtered.filter(i => i.domain === d)
    })).filter(g => g.items.length > 0);
    body = <div className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-1128">
        <div className="max-w-[640px] mx-auto w-full" data-id="element-1129">
          <header className="mb-6" data-id="element-1130">
            <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight" data-id="element-1131">Integrations</h1>
            <p className="text-[13px] text-[#8a8680] mt-1" data-id="element-1132">Accounts and services ZenBot has access to.</p>
          </header>

          <div className="relative mb-8" data-id="element-1133">
            <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#8a8680]/40" data-id="element-1134" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search integrations" className="w-full bg-transparent text-[#e8e4df] pl-6 pr-4 py-2 text-[14px] outline-none placeholder:text-[#8a8680]/30 border-b border-[#8a8680]/10 focus:border-[#8a8680]/30 transition-colors" data-id="element-1135" />
          </div>

          <div className="flex flex-col gap-10" data-id="element-1136">
            {grouped.map(({
            domain,
            label,
            items
          }) => <section key={domain} data-id="element-1137">
                <h2 className="text-[12px] text-[#8a8680]/50 mb-4" data-id="element-1138">{label}</h2>
                <div className="flex flex-col gap-3" data-id="element-1139">
                  {items.map((integ, i) => {
                const Icon = integ.icon;
                const showAsConnected = integ.status === 'connected' || justConnected.has(integ.id);
                return <motion.button key={integ.id} initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  duration: 0.3,
                  delay: i * 0.03
                }} onClick={() => !integ.disabled && setViewingId(integ.id)} disabled={integ.disabled} className={`bg-[#1c1c22] rounded-[16px] p-5 text-left group hover:bg-[#1e1e25] transition-colors w-full ${integ.disabled ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-1140">
                        <div className="flex items-center gap-4" data-id="element-1141">
                          <div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center shrink-0" data-id="element-1142">
                            <Icon size={18} className={showAsConnected ? 'text-[#e8e4df]' : 'text-[#8a8680]/50'} strokeWidth={1.5} data-id="element-1143" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1144">
                            <div className="flex items-center gap-2 flex-wrap" data-id="element-1145">
                              <span className={`text-[15px] ${showAsConnected ? 'text-[#e8e4df]' : 'text-[#e8e4df]/70'}`} data-id="element-1146">{integ.name}</span>
                              {integ.authKind === 'api-key' && <span className="text-[9px] text-[#8a8680]/60 border border-[#8a8680]/20 rounded-full px-1.5 py-0.5 uppercase tracking-wide" data-id="element-1147">API key</span>}
                            </div>
                            <p className="text-[12px] text-[#8a8680]/60 mt-0.5 truncate" data-id="element-1148">{integ.syncDescription}</p>
                          </div>
                          {showAsConnected ? <div className="w-1.5 h-1.5 rounded-full bg-[#4ac0d4] shrink-0" data-id="element-1149" /> : integ.disabled ? <span className="text-[11px] text-[#8a8680]/40 shrink-0" data-id="element-1150">{integ.disabledReason}</span> : <span className="text-[12px] text-[#8a8680]/40 group-hover:text-[#8a8680] shrink-0" data-id="element-1151">Connect</span>}
                        </div>
                      </motion.button>;
              })}
                </div>
              </section>)}
          </div>

          <p className="text-[11px] text-[#8a8680]/40 mt-12 text-center" data-id="element-1152">
            Need a service we don't support yet? Raise a Nango integration request.
          </p>
        </div>
      </div>;
  }
  return <>
      {body}
      <ConnectSheet integration={connectingIntegration} onClose={() => setConnectingId(null)} onConnected={id => {
      setJustConnected(prev => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      setConnectingId(null);
    }} data-id="element-1153" />
    </>;
}
function IntegrationDetail({
  integration,
  justConnected,
  onBack,
  onConnect
}: {
  integration: Integration;
  justConnected: boolean;
  onBack: () => void;
  onConnect: () => void;
}) {
  const Icon = integration.icon;
  const [revealed, setRevealed] = useState(false);
  const connected = integration.status === 'connected' || justConnected;
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.3
  }} className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-1154">
      <div className="max-w-[640px] mx-auto w-full" data-id="element-1155">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680]/60 hover:text-[#e8e4df] transition-colors -ml-1 mb-8" data-id="element-1156">
          <ArrowLeft size={14} data-id="element-1157" />
          <span className="text-[13px]" data-id="element-1158">Integrations</span>
        </button>

        <header className="mb-6 flex items-center gap-4" data-id="element-1159">
          <div className="w-12 h-12 rounded-[14px] bg-[#252530] flex items-center justify-center" data-id="element-1160">
            <Icon size={22} className="text-[#e8e4df]" strokeWidth={1.5} data-id="element-1161" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-1162">
            <div className="flex items-center gap-2" data-id="element-1163">
              <h1 className="text-[20px] font-medium text-[#e8e4df]" data-id="element-1164">{integration.name}</h1>
              {connected && <div className="w-1.5 h-1.5 rounded-full bg-[#4ac0d4]" data-id="element-1165" />}
            </div>
            <div className="flex items-center gap-2 mt-0.5 text-[12px] text-[#8a8680] flex-wrap" data-id="element-1166">
              <span data-id="element-1167">
                {connected ? `Connected via ${integration.authKind === 'api-key' ? 'API key' : 'Nango'}` : 'Not connected'}
              </span>
              {integration.lastSynced && connected && <>
                  <span className="text-[#8a8680]/30" data-id="element-1168">·</span>
                  <span data-id="element-1169">last synced {integration.lastSynced}</span>
                </>}
            </div>
          </div>
        </header>

        <p className="text-[13px] text-[#8a8680] leading-[1.6] mb-6" data-id="element-1170">{integration.syncDescription}</p>

        <div className="bg-[#1c1c22] rounded-[16px] p-5 mb-4" data-id="element-1171">
          <h3 className="text-[11px] text-[#8a8680]/60 uppercase tracking-wide mb-3" data-id="element-1172">Connection</h3>
          {connected ? <>
              <div className="flex items-center justify-between py-1.5" data-id="element-1173">
                <span className="text-[12px] text-[#8a8680]" data-id="element-1174">Account</span>
                <span className="text-[13px] text-[#e8e4df]" data-id="element-1175">{integration.account}</span>
              </div>
              {integration.connectionId && <div className="flex items-center justify-between py-1.5" data-id="element-1176">
                  <span className="text-[12px] text-[#8a8680]" data-id="element-1177">Connection id</span>
                  <div className="flex items-center gap-2" data-id="element-1178">
                    <span className="text-[12px] text-[#8a8680]/70 font-mono" data-id="element-1179">
                      {revealed ? integration.connectionId : `${integration.connectionId.slice(0, 10)}${'\u2022'.repeat(5)}`}
                    </span>
                    <button onClick={() => setRevealed(!revealed)} className="text-[#8a8680]/40 hover:text-[#8a8680]" data-id="element-1180">
                      {revealed ? <EyeOff size={12} data-id="element-1181" /> : <Eye size={12} data-id="element-1182" />}
                    </button>
                  </div>
                </div>}
              <div className="flex items-center gap-4 mt-4" data-id="element-1183">
                <button className="flex items-center gap-1.5 text-[12px] text-[#8a8680] hover:text-[#e8e4df] transition-colors" data-id="element-1184">
                  <RefreshCw size={11} data-id="element-1185" />
                  Rotate credentials
                </button>
                <button className="text-[12px] text-[#8a8680]/40 hover:text-[#8a8680] transition-colors" data-id="element-1186">
                  Disconnect
                </button>
              </div>
            </> : <>
              <p className="text-[12px] text-[#8a8680] mb-4" data-id="element-1187">
                Connect this service to sync data and enable actions.
              </p>
              <button onClick={onConnect} className="text-[13px] text-[#4ac0d4] hover:text-[#5bd0dc] transition-colors" data-id="element-1188">
                Connect {integration.name}
              </button>
            </>}
        </div>

        {connected && integration.scopes && integration.scopes.length > 0 && <div className="bg-[#1c1c22] rounded-[16px] p-5 mb-4" data-id="element-1189">
            <h3 className="text-[11px] text-[#8a8680]/60 uppercase tracking-wide mb-3" data-id="element-1190">
              {integration.authKind === 'api-key' ? 'Capabilities' : 'Permissions'}
            </h3>
            {integration.scopes.map((scope, i) => <div key={i} className="flex items-center gap-2 py-1.5" data-id="element-1191">
                <ShieldCheck size={13} className="text-[#4ac0d4]/70" data-id="element-1192" />
                <span className="text-[13px] text-[#e8e4df]/80" data-id="element-1193">{scope.label}</span>
              </div>)}
          </div>}

        {connected && integration.syncs && integration.syncs.length > 0 && <div className="bg-[#1c1c22] rounded-[16px] p-5 mb-4" data-id="element-1194">
            <h3 className="text-[11px] text-[#8a8680]/60 uppercase tracking-wide mb-3" data-id="element-1195">Syncs</h3>
            {integration.syncs.map((sync, i) => <div key={i} className="flex items-center justify-between py-2 border-t border-white/[0.03] first:border-0" data-id="element-1196">
                <div data-id="element-1197">
                  <div className="text-[13px] text-[#e8e4df]" data-id="element-1198">{sync.label}</div>
                  <div className="text-[11px] text-[#8a8680]/60 mt-0.5" data-id="element-1199">
                    last {sync.lastRun} · next {sync.nextRun}
                  </div>
                </div>
                <div className="text-[12px] text-[#8a8680]" data-id="element-1200">{sync.records.toLocaleString()}</div>
              </div>)}
          </div>}

        {connected && integration.actions && integration.actions.length > 0 && <div className="bg-[#1c1c22] rounded-[16px] p-5 mb-4" data-id="element-1201">
            <h3 className="text-[11px] text-[#8a8680]/60 uppercase tracking-wide mb-3" data-id="element-1202">Actions</h3>
            {integration.actions.map((action, i) => <div key={i} className="py-2 border-t border-white/[0.03] first:border-0" data-id="element-1203">
                <div className="text-[13px] text-[#e8e4df]" data-id="element-1204">{action.label}</div>
                <div className="text-[11px] text-[#8a8680]/60 mt-0.5" data-id="element-1205">{action.description}</div>
              </div>)}
          </div>}

        {connected && integration.outputRouting && integration.outputRouting.length > 0 && <div className="bg-[#1c1c22] rounded-[16px] p-5 mb-4" data-id="element-1206">
            <h3 className="text-[11px] text-[#8a8680]/60 uppercase tracking-wide mb-3" data-id="element-1207">Data routes to</h3>
            <div className="flex items-center gap-2 flex-wrap" data-id="element-1208">
              {integration.outputRouting.map(r => <span key={r} className="text-[12px] text-[#8a8680] capitalize bg-white/[0.03] px-2.5 py-1 rounded-full" data-id="element-1209">
                  {r}
                </span>)}
            </div>
          </div>}
      </div>
    </motion.div>;
}
function ConnectSheet({
  integration,
  onClose,
  onConnected
}: {
  integration: Integration | null;
  onClose: () => void;
  onConnected: (id: string) => void;
}) {
  const [phase, setPhase] = useState<'opening' | 'done'>('opening');
  useEffect(() => {
    if (!integration) return;
    setPhase('opening');
    const t = setTimeout(() => setPhase('done'), 1500);
    return () => clearTimeout(t);
  }, [integration?.id]);
  useEffect(() => {
    if (phase === 'done' && integration) {
      const id = integration.id;
      const t = setTimeout(() => onConnected(id), 800);
      return () => clearTimeout(t);
    }
  }, [phase, integration, onConnected]);
  return <AnimatePresence data-id="element-1210">
      {integration && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/60 z-[70]" onClick={onClose} data-id="element-1211" />
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 32,
        stiffness: 280
      }} className="fixed inset-x-0 bottom-0 z-[71] bg-[#141418] rounded-t-[20px] border-t border-white/[0.06]" style={{
        maxHeight: '60vh'
      }} data-id="element-1212">
            <div className="flex justify-center pt-2 pb-1" data-id="element-1213">
              <div className="w-10 h-1 rounded-full bg-[#8a8680]/30" data-id="element-1214" />
            </div>
            <div className="px-8 py-8 flex flex-col items-center text-center" data-id="element-1215">
              {phase === 'opening' ? <>
                  <div className="w-5 h-5 border-2 border-[#4ac0d4]/30 border-t-[#4ac0d4] rounded-full animate-spin mb-4" data-id="element-1216" />
                  <h2 className="text-[15px] text-[#e8e4df] mb-1" data-id="element-1217">Opening Nango Connect</h2>
                  <p className="text-[12px] text-[#8a8680]" data-id="element-1218">
                    You'll finish the OAuth flow in a new window
                  </p>
                </> : <>
                  <div className="w-12 h-12 rounded-full bg-[#4ac0d4]/15 border border-[#4ac0d4]/30 flex items-center justify-center mb-4" data-id="element-1219">
                    <ShieldCheck size={22} className="text-[#4ac0d4]" strokeWidth={1.8} data-id="element-1220" />
                  </div>
                  <h2 className="text-[15px] text-[#e8e4df] mb-1" data-id="element-1221">{integration.name} connected</h2>
                  <p className="text-[12px] text-[#8a8680]" data-id="element-1222">Nango is syncing data now</p>
                </>}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}